import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  BLISS_BASIC_CONFIGURATOR_STEPS,
  blissBasicConfiguratorFlow,
  isBasicPaintColor,
  defaultBlissBasicConfiguratorSelections,
  loadBlissBasicConfiguratorDraft,
  saveBlissBasicConfiguratorDraft,
  type BlissBasicConfiguratorStepId,
  type BlissBasicConfiguratorSelections,
  type BlissBasicFinishId,
  type BlissBasicFinishTreatment,
} from '../../config/blissBasicConfigurator'
import type { BlissBasicConfiguratorStepDef } from '../../config/blissBasicCatalog'
import { fetchBlissBasicPublicCatalog } from '../../services/blissBasicCatalogClient'
import { blissBasicFinishHandoffUrl } from '../../config/blissDesignHost'
import { trackBlissFunnelEvent } from '../../services/funnelAnalytics'
import { fetchBlissRoomPlan } from '../../services/blissRoomPlanService'
import {
  BLISS_DESIGN_HANDOFF_SOURCE,
  BLISS_ROOM_PLAN_CONTEXT_SESSION_KEY,
  BLISS_ROOM_PLAN_SESSION_KEY,
} from '../../config/blissRoomPlan'
import {
  applyBlissRoomPlanToBasicSelections,
  applyBlissRoomPlanToLeadGate,
} from '../../utils/applyBlissRoomPlanPrefill'
import { defaultConfiguratorStepDefs, isConfiguratorSelectionStep } from '../../utils/blissBasicConfiguratorDefaults'
import {
  applyStepSelection,
  selectedKeysForStep,
  stepRequiresSelection,
} from '../../utils/blissBasicConfiguratorSelection'
import BlissBasicExpandableOptions from './BlissBasicExpandableOptions'
import { BlissBasicMattressSizeLegend } from './BlissBasicMattressLayoutViz'
import './BlissBasicConfigurator.css'

function splitPrefillName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

function prefillFromSearchParams(params: URLSearchParams): Partial<BlissBasicConfiguratorSelections> {
  const name = params.get('name') || ''
  const parts = splitPrefillName(name)
  return {
    firstName: parts.firstName,
    lastName: parts.lastName,
    email: params.get('email')?.trim() || '',
    projectZip: params.get('zip')?.trim() || '',
    linkedQuoteRequestId: params.get('bliss_quote_id')?.trim() || undefined,
  }
}

function stepMeta(
  id: BlissBasicConfiguratorStepId,
  catalogSteps: BlissBasicConfiguratorStepDef[],
): { title: string; subtitle: string } {
  const fromCatalog = catalogSteps.find((s) => s.id === id)
  const fallback = BLISS_BASIC_CONFIGURATOR_STEPS.find((s) => s.id === id)
  return {
    title: fromCatalog?.title ?? fallback?.title ?? id,
    subtitle: fromCatalog?.subtitle ?? fallback?.subtitle ?? '',
  }
}

export default function BlissBasicConfigurator() {
  const [searchParams] = useSearchParams()
  const urlPrefill = useMemo(() => prefillFromSearchParams(searchParams), [searchParams])

  const [catalogSteps, setCatalogSteps] = useState<BlissBasicConfiguratorStepDef[]>(() =>
    defaultConfiguratorStepDefs(),
  )

  const [selections, setSelections] = useState<BlissBasicConfiguratorSelections>(() => {
    const draft = loadBlissBasicConfiguratorDraft()
    return defaultBlissBasicConfiguratorSelections({ ...urlPrefill, ...draft, ...urlPrefill })
  })

  const skipLayout =
    searchParams.get('skip_layout') === '1' ||
    (searchParams.get('source') === BLISS_DESIGN_HANDOFF_SOURCE &&
      (Boolean(searchParams.get('package_id')) || Boolean(searchParams.get('finish_treatment'))))

  const flow = useMemo(
    () => blissBasicConfiguratorFlow(selections, { skipLayout }),
    [selections, skipLayout],
  )
  const [stepIndex, setStepIndex] = useState(0)
  const stepId = flow[stepIndex] ?? 'layout'

  useEffect(() => {
    void fetchBlissBasicPublicCatalog().then((catalog) => {
      if (!catalog?.configuratorSteps) return
      setCatalogSteps(catalog.configuratorSteps)
    })
  }, [])

  useEffect(() => {
    saveBlissBasicConfiguratorDraft(selections)
  }, [selections])

  useEffect(() => {
    trackBlissFunnelEvent('bliss_basic_configurator_view', { skip_layout: skipLayout })
  }, [skipLayout])

  useEffect(() => {
    const treatment = searchParams.get('finish_treatment')?.trim() as BlissBasicFinishTreatment | undefined
    const finishFromUrl = searchParams.get('finish_id')?.trim() as BlissBasicFinishId | undefined
    if (treatment !== 'painted_stained' && treatment !== 'unfinished_raw') return
    setSelections((prev) => ({
      ...prev,
      finishTreatment: treatment,
      ...(finishFromUrl ? { finishId: finishFromUrl } : {}),
      notes: prev.notes?.includes('Bliss Basic finish:')
        ? prev.notes
        : [
            prev.notes?.trim(),
            treatment === 'unfinished_raw'
              ? 'Bliss Basic finish: Unfinished / raw'
              : `Bliss Basic finish: Painted${finishFromUrl ? ` (${finishFromUrl})` : ''}`,
          ]
            .filter(Boolean)
            .join('\n'),
    }))
  }, [searchParams])

  useEffect(() => {
    const planId = searchParams.get('plan_id')?.trim()
    if (searchParams.get('source') !== BLISS_DESIGN_HANDOFF_SOURCE || !planId) return
    void fetchBlissRoomPlan(planId).then((plan) => {
      if (!plan) return
      setSelections((prev) => applyBlissRoomPlanToBasicSelections(plan, prev))
      try {
        sessionStorage.setItem(BLISS_ROOM_PLAN_SESSION_KEY, planId)
        sessionStorage.setItem(
          BLISS_ROOM_PLAN_CONTEXT_SESSION_KEY,
          JSON.stringify(applyBlissRoomPlanToLeadGate(plan).projectContext),
        )
      } catch {
        /* ignore */
      }
      trackBlissFunnelEvent('bliss_basic_plan_prefill', { plan_id: planId })
    })
  }, [searchParams])

  useEffect(() => {
    if (stepIndex >= flow.length) {
      setStepIndex(Math.max(0, flow.length - 1))
    }
  }, [flow.length, stepIndex])

  useEffect(() => {
    if (flow.length > 0) return
    window.location.assign(blissBasicFinishHandoffUrl(searchParams.toString()))
  }, [flow.length, searchParams])

  const patch = useCallback((partial: Partial<BlissBasicConfiguratorSelections>) => {
    setSelections((prev) => ({ ...prev, ...partial }))
  }, [])

  const activeStepDef = useMemo(() => {
    const step = catalogSteps.find((s) => s.id === stepId)
    if (!step) return undefined
    return {
      ...step,
      items: step.items.map((item) => ({
        ...item,
        priceUsd: null,
        priceHighUsd: null,
        priceLabel: null,
      })),
    }
  }, [catalogSteps, stepId])

  const canAdvance = useMemo(() => {
    if (stepId === 'finish') return isBasicPaintColor(selections.finishId)
    if (isConfiguratorSelectionStep(stepId) && stepRequiresSelection(stepId)) {
      return selectedKeysForStep(stepId, selections).length > 0
    }
    return true
  }, [stepId, selections])

  const finishConfigurator = () => {
    saveBlissBasicConfiguratorDraft(selections)
    window.location.assign(blissBasicFinishHandoffUrl(searchParams.toString()))
  }

  const onNext = () => {
    if (!canAdvance) return
    if (stepIndex >= flow.length - 1) {
      finishConfigurator()
      return
    }
    setStepIndex((i) => Math.min(i + 1, flow.length - 1))
  }

  const meta = stepMeta(stepId, catalogSteps)
  const progressLabel = `${Math.min(stepIndex + 1, flow.length)} / ${flow.length}`

  return (
    <div className="bb-config">
      <header className="bb-config-header">
        <Link className="bb-config-back" to="/">
          ← Back
        </Link>
        <span className="bb-config-progress">{progressLabel}</span>
      </header>

      <main className="bb-config-main">
        <h1 className="bb-config-title">{meta.title}</h1>
        <p className="bb-config-sub">{meta.subtitle}</p>
        {stepId === 'finish' && !isBasicPaintColor(selections.finishId) && <p>Your previous shade or stain is part of Bliss Custom. Choose a Basic paint color here, or explore Custom in the Design Studio.</p>}

        {stepId === 'layout' && <BlissBasicMattressSizeLegend />}

        {isConfiguratorSelectionStep(stepId) && activeStepDef && (
          <BlissBasicExpandableOptions
            items={activeStepDef.items}
            multiSelect={activeStepDef.multiSelect}
            selectedKeys={selectedKeysForStep(stepId, selections)}
            onChange={(keys) => applyStepSelection(stepId, keys, patch)}
            ariaLabel={meta.title}
            showPrices={false}
            mattressLayoutMode={stepId === 'layout'}
          />
        )}

      </main>

      <footer className="bb-config-footer">
        <button type="button" className="bb-config-next" disabled={!canAdvance} onClick={onNext}>
          {stepIndex >= flow.length - 1 ? 'Continue to Design Studio →' : 'Next →'}
        </button>
      </footer>
    </div>
  )
}
